/**
 * Git history of a project's repositories (AIOE 0.65.0), read through the project's GitHub connection: the commit
 * graph with its branches, who each commit's work came from (an agent or a person), one commit's diff, two refs
 * compared, and a pull request's files. AIOE-owned. The shapes match the workbench's own git payloads so its models
 * read them unchanged: `GraphRefPayload`, `GraphCommitPayload`, `CommitGraphPayload` and `CommitDetailPayload` (its
 * shared/protocol/messages.ts), `PullRequestFile` (its shared/protocol/pullRequests.ts) and `GitTagsPayload` (its
 * shared/git/tags.ts, mirrored here as ../git/tags.ts). What only a clone knows (sync, the working changes,
 * worktrees, the upstream, fetch, a shallow clone, remote refs) is never sent. Fields added here are optional.
 *
 *   GET /projects/v1/:id/roots/:rootId/git/commits?branch=&since=&until=&path=&limit=&page=   ProjectGitCommits
 *       No `branch`: the graph of every branch the platform reads (the default branch first, at most
 *       GIT_GRAPH_BRANCHES_MAX), merged child before parent, each commit with the branches it was read on and where
 *       it stands (`stage`). `since`/`until` are ISO times, `path` a file or folder. `page` counts from 1.
 *   GET /projects/v1/:id/roots/:rootId/git/commits/:sha                                       ProjectGitCommitDetail
 *       The commit with its files and their patches (GIT_PATCH_* say how much).
 *   GET /projects/v1/:id/roots/:rootId/git/branches                                           ProjectGitBranches
 *   GET /projects/v1/:id/roots/:rootId/git/tags                                               ProjectGitTags
 *   GET /projects/v1/:id/roots/:rootId/git/compare?base=&head=                                ProjectGitCompare
 *   GET /projects/v1/:id/pull-requests/:key/files                                             ProjectPullRequestFiles
 *       `key` is the pull request's key (`github:owner/name:12`), or its number when the project has one GitHub
 *       repository (else `?repository=owner/name`).
 *
 * Who: viewers and above, as for every project read (a sign-in, a member's desktop, a node naming its member). The
 * read goes out with the reader's own linked GitHub account, else the organisation's (the GitHub App's installation
 * token, else its organisation token): `repository.readWith` says which. Answers are cached for a short while per
 * organisation, connection, reader and address, and revalidated with GitHub's ETag (a 304 costs no rate limit); a
 * commit named by its full id, and two full ids compared, never change, so they are kept for a day.
 *
 * Refusals, in words (`message`):
 *   404 not_found | unknown_root          not the caller's project, or a repository it does not have
 *   409 no_github                         the project names no GitHub connection, or this repository is not one of
 *                                         its GitHub repositories (the console says how to connect one)
 *   409 not_linked                        no credential to read with: link a GitHub account under Settings, You
 *   400 invalid_request                   a ref, id, time or number that is not one
 *   404 unknown_ref                       GitHub has no such commit, branch or tag, or the connection cannot see it
 *   422 diff_too_large                    GitHub would not build this diff
 *   429 rate_limited { resetAt? }         GitHub is limiting requests (403 or 429 from it, said as such)
 *   502 provider                          GitHub refused or failed, in a fixed sentence (never its reply's text)
 */
import { z } from "zod";
/** Commits in one page of one branch: GitHub's own ceiling for `per_page`. */
export const GIT_COMMITS_PAGE_MAX = 100;
export const GIT_COMMITS_PAGE_DEFAULT = 50;
/** Branches read for the graph of every branch (the default branch, then the workbench's own, then the rest). */
export const GIT_GRAPH_BRANCHES_MAX = 12;
/** Files listed for a commit, a comparison or a pull request (GitHub sends at most 300 for a commit and a comparison). */
export const GIT_FILES_MAX = 300;
/** The longest one file's patch is sent; a longer one is left out with `patchLeftOut: "too-large"`. */
export const GIT_PATCH_FILE_MAX_CHARS = 200_000;
/** The most patch text in one answer; files after that go without theirs (`patchLeftOut: "budget"`, `patchTruncated`). */
export const GIT_PATCH_TOTAL_MAX_CHARS = 1_000_000;
/** A branch, tag or commit id as git takes it: no spaces, no `..`, none of `~^:?*[\`, not starting with `-` or `/`. */
export const gitRefSchema = z
    .string()
    .min(1)
    .max(250)
    .regex(/^[^\s~^:?*[\\]+$/, "not a branch, tag or commit")
    .refine((r) => !r.includes("..") && !r.startsWith("-") && !r.startsWith("/") && !r.endsWith("/") && !r.endsWith(".lock") && !r.includes("@{"), "not a branch, tag or commit");
/** A commit id, whole or abbreviated. */
export const gitShaSchema = z.string().regex(/^[0-9a-f]{4,40}$/i, "not a commit id");
const isoTime = z.string().max(40).refine((t) => !Number.isNaN(Date.parse(t)), "not a time");
export const gitCommitsQuerySchema = z.object({
    branch: gitRefSchema.optional(),
    since: isoTime.optional(),
    until: isoTime.optional(),
    /** A file or folder in the repository. */
    path: z
        .string()
        .min(1)
        .max(500)
        .refine((p) => !p.split("/").includes(".."), "not a path in the repository")
        .optional(),
    limit: z.coerce.number().int().min(1).max(GIT_COMMITS_PAGE_MAX).default(GIT_COMMITS_PAGE_DEFAULT),
    page: z.coerce.number().int().min(1).max(100).default(1),
});
export const gitCompareQuerySchema = z.object({ base: gitRefSchema, head: gitRefSchema });
/** A branch's kind by the workbench's names, given the repository's default branch. */
export function gitBranchKind(name, defaultBranch) {
    if (name === defaultBranch)
        return { kind: "base" };
    if (name.startsWith("mojoup/integration/"))
        return { kind: "integration" };
    const agent = /^mojoup\/([^/]+)\/([^/@]+)(?:@([^/@]+))?$/.exec(name);
    if (agent)
        return { kind: "agent", agent: { teamSlug: agent[1], member: agent[2], ...(agent[3] ? { planSlug: agent[3] } : {}) } };
    if (name.startsWith("feat/"))
        return { kind: "plan" };
    return { kind: "other" };
}
/** Where a commit stands, from the kinds of the branches it was read on: in the default branch, on an integration branch, in a plan's branch, or only being worked on. */
export function gitStageOf(kinds) {
    if (kinds.includes("base"))
        return "main";
    if (kinds.includes("integration"))
        return "landing";
    if (kinds.includes("plan"))
        return "plan";
    return "working";
}
//# sourceMappingURL=projectGit.js.map